import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { isNotFoundError } from '@/lib/utils/error.utils';
import { usePublicPage } from '../hooks/usePublicPage';
import { PageSections } from './PageSections';

/**
 * Shared loading/error/success shell for every public marketing page
 * (Home, Benefits, Investors, Trusted Partners, Due Diligence Platform) -
 * extracted from 5 near-identical page components so the states below are
 * defined once, not drifted across 5 copies.
 *
 * A page the public read API 404s on (unpublished, or an unknown slug -
 * FR-104) is an expected, neutral state, not a failure: an admin simply
 * hasn't published it yet. Rendering it through the same red `ErrorState`
 * used for a real failure (a 500, a network error) read as "something is
 * broken" on staging when a page was just awaiting its publish toggle.
 * `isNotFoundError` tells the two apart; only a non-404 failure gets the
 * alarming treatment.
 */
export function PublicPageLoader({ slug }: { slug: string }) {
  const { data, isPending, isError, error } = usePublicPage(slug);

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <LoadingSpinner size="lg" label="Loading" />
      </div>
    );
  }

  if (isError && isNotFoundError(error)) {
    return (
      <div className="py-24">
        <EmptyState title="This page isn't available yet" description="Check back soon." />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="py-24">
        <ErrorState title="Couldn't load this page" description="Please try again shortly." />
      </div>
    );
  }

  return <PageSections sections={data.sections} pageSlug={slug} />;
}
